import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, X, Loader2, Trash2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { formatDistanceToNow } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export const Announcements = ({ isAdmin }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pin: true });
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => {
    api.get("/announcements").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const post = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and message required");
    setSaving(true);
    try {
      const { data } = await api.post("/announcements", form);
      if (form.pin && data?.id) setItems((prev) => [data, ...prev]);
      setOpen(false);
      const wasPinned = form.pin;
      setForm({ title: "", body: "", pin: true });
      toast.success(wasPinned ? "Announcement posted — partners notified" : "Email sent to all partners");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/announcements/${delTarget.id}`);
      setItems((prev) => prev.filter((x) => x.id !== delTarget.id));
      toast.success("Announcement removed");
      setDelTarget(null);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!loaded) return null;
  // Partners with no announcements: hide entirely
  if (!isAdmin && items.length === 0) return null;

  return (
    <div data-testid="announcements-section" className="mb-1">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[var(--brand-navy)]">
          <Megaphone className="h-4 w-4" />
          <span className="text-xs uppercase tracking-widest font-semibold">Special announcements</span>
        </div>
        {isAdmin && (
          <button data-testid="add-announcement-btn" onClick={() => setOpen(true)}
            className="flex items-center gap-1 text-xs font-semibold accent-text hover:opacity-80 transition-opacity">
            <Plus className="h-3.5 w-3.5" /> Announcement
          </button>
        )}
      </div>

      {items.length === 0 && isAdmin ? (
        <button data-testid="announcements-empty" onClick={() => setOpen(true)}
          className="w-full rounded-2xl border-2 border-dashed border-[var(--brand-navy)]/20 p-4 text-sm text-muted-foreground hover:bg-[var(--brand-navy)]/5 transition-colors text-left">
          Post an important update partners shouldn't miss — it pins here and notifies everyone.
        </button>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} data-testid="announcement-card"
              className="relative rounded-2xl p-4 text-white shadow-[0_4px_24px_rgba(0,82,122,0.15)]"
              style={{ background: "linear-gradient(135deg,var(--brand-navy),var(--brand-navy-dark))" }}>
              <div className="flex items-start gap-2">
                <Megaphone className="h-4 w-4 mt-1 shrink-0 accent-text" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-lg leading-tight">{a.title}</div>
                  <p className="text-sm text-white/85 mt-1 whitespace-pre-line">{a.body}</p>
                  <div className="text-[11px] text-white/50 mt-2">
                    {a.author_name} · {a.created_at ? formatDistanceToNow(new Date(a.created_at), { addSuffix: true }) : ""}
                  </div>
                </div>
                {isAdmin && (
                  <button data-testid="announcement-delete-btn" onClick={() => setDelTarget(a)}
                    className="shrink-0 h-7 w-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[var(--brand-navy)]">New announcement</DialogTitle>
            <DialogDescription>Sent to all partners via email{form.pin ? " + push, and pinned to Home" : ""}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Title</Label>
              <Input data-testid="announcement-title" className="mt-1.5 rounded-xl h-11" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Departure delayed to Aug 4" />
            </div>
            <div>
              <Label className="text-sm">Message</Label>
              <Textarea data-testid="announcement-body" className="mt-1.5 rounded-xl min-h-24" value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Share the important details…" />
            </div>
            <div className="flex items-center justify-between rounded-xl bg-[var(--brand-navy)]/5 px-3 py-2.5">
              <div>
                <Label className="text-sm">Pin to Home &amp; push</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">{form.pin ? "Pinned to everyone's Home + push notification." : "Email only — nothing pinned, no push."}</p>
              </div>
              <Switch data-testid="announcement-pin-switch" checked={form.pin} onCheckedChange={(v) => setForm({ ...form, pin: v })} />
            </div>
            <Button data-testid="announcement-submit-btn" disabled={saving} onClick={post}
              className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.pin ? "Post & notify partners" : "Email all partners")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="announcement-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[var(--brand-navy)]">Remove announcement?</AlertDialogTitle>
            <AlertDialogDescription>"{delTarget?.title}" will be unpinned from everyone's Home screen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="announcement-delete-confirm" onClick={(e) => { e.preventDefault(); remove(); }}
              className="rounded-full bg-red-600 text-white hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Announcements;
